/**
 * Map (hyperdigital/map) - Leaflet with OpenStreetMap tiles.
 *
 * Tiles are third party requests, so the map is only built after consent when the element asks for
 * it; until then the template shows its own placeholder and nothing leaves the visitor's browser.
 *
 * Markers use L.divIcon rather than Leaflet's default image marker: the bundled default icon
 * resolves its PNGs relative to the stylesheet, which breaks as soon as the CSS is bundled. A
 * divIcon is our own markup, needs no image files and can be styled with the theme's colours.
 *
 * The list next to the map and the markers drive each other, and the list stays the accessible
 * representation - a Leaflet map is not keyboard navigable in any useful way, so the addresses,
 * links and the "show on map" buttons live in real HTML.
 */
document.addEventListener('alpine:init', () => {
    Alpine.data('hdMap', () => ({
        map: null,
        markers: [],
        active: null,
        ready: false,

        init() {
            // Without a consent gate the map may build immediately.
            if (this.$root.dataset.consent !== '1') {
                this.$nextTick(() => this.build());
            }
        },

        /** Called by the consent button in the template. */
        accept() {
            this.$root.dataset.consent = '0';
            this.$nextTick(() => this.build());
        },

        build() {
            if (this.ready) {
                return;
            }
            const L = window.L;
            const canvas = this.$refs.canvas;
            if (!L || !canvas) {
                console.warn('[Map] Leaflet not loaded - the list of locations is still readable');
                return;
            }

            const locations = this.locations();
            if (!locations.length) {
                return;
            }

            this.map = L.map(canvas, {
                scrollWheelZoom: false,   // never steal the page scroll
                attributionControl: true,
            });

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            }).addTo(this.map);

            this.markers = locations.map((location, index) => {
                const marker = L.marker([location.lat, location.lng], {
                    icon: L.divIcon({
                        className: 'hdmap-pin',
                        html: '<span class="hdmap-pin-dot">' + (index + 1) + '</span>',
                        iconSize: [32, 32],
                        // The pin's tip is the corner of a 32px square rotated by 45deg, which
                        // reaches 16 * sqrt(2) ~ 22.6px below the centre - not the box edge.
                        iconAnchor: [16, 38],
                        // Relative to the anchor: open above the round head (~6.6px over the box top).
                        popupAnchor: [0, -44],
                    }),
                    title: location.title,
                    alt: location.title,
                    keyboard: false,
                }).addTo(this.map);

                if (location.popup) {
                    marker.bindPopup(location.popup);
                }
                marker.on('click', () => { this.active = index; });
                return marker;
            });

            this.fit();
            this.ready = true;

            // The element is often inside a tab or an accordion; recalculate once it becomes visible.
            if ('ResizeObserver' in window) {
                new ResizeObserver(() => this.map && this.map.invalidateSize()).observe(canvas);
            }
        },

        /** Read the locations from the DOM, so the markup stays the single source of truth. */
        locations() {
            return Array.from(this.$root.querySelectorAll('[data-location]')).map((el) => ({
                lat: Number.parseFloat(el.dataset.lat),
                lng: Number.parseFloat(el.dataset.lng),
                title: el.dataset.title || '',
                popup: el.dataset.popup || '',
            })).filter((l) => Number.isFinite(l.lat) && Number.isFinite(l.lng));
        },

        fit() {
            const points = this.markers.map((m) => m.getLatLng());
            if (!points.length) {
                return;
            }
            if (points.length === 1) {
                this.map.setView(points[0], Number.parseInt(this.$root.dataset.zoom, 10) || 14);
                return;
            }
            this.map.fitBounds(window.L.latLngBounds(points), { padding: [40, 40] });
        },

        /** "Show on map" in the list. */
        show(index) {
            this.active = index;
            const marker = this.markers[index];
            if (!marker || !this.map) {
                return;
            }
            this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 14), {
                animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
            });
            marker.openPopup();
        },

        isActive(index) {
            return this.active === index;
        },

        destroy() {
            if (this.map) {
                this.map.remove();
            }
        },
    }));
});
